import api from '@/api';
import dayjs from 'dayjs';

/**
 * @class ClientAction
 */
class ClientAction {
  /**
   * Get a list of client actions
   *
   * @param params
   * @param cancelSource
   * @returns {AxiosPromise<any>}
   */
  static list(params, cancelSource = null) {
    if (cancelSource) {
      params.cancelToken = cancelSource.token;
    }

    return api.get('/actions/client', params);
  }

  /**
   * Add a client action
   * @param params
   * @returns {*}
   */
  static add(params) {
    return api.post('/actions/client', params);
  }

  /**
   * Update a client action
   * @param clientActionId
   * @param params
   * @returns {*}
   */
  static update(clientActionId, params) {
    return api.put(`/actions/client/${clientActionId}`, params);
  }

  /**
   * Retrieve an action by its clientActionId
   *
   * @param id
   * @returns {*}
   */
  static find(id) {
    return api.get(`/actions/client/${id}`);
  }

  /**
   * Gets counts for actions key'd by status
   * @param propertyTenure
   *
   * @returns {AxiosPromise<any>}
   */
  static getCounts(propertyTenure) {
    return api.get('/actions/client/counts', { params: { propertyTenure } });
  }

  /**
   * Returns tags
   * @param {boolean} includeInactive - Whether to include inactive tags in the response
   * @returns {AxiosPromise<any>}
   */
  static getTags({ includeInactive = false } = {}) {
    const params = includeInactive ? { includeInactive: 1 } : {};
    return api.get('/actions/client/tags', { params });
  }

  /**
   * Returns tags for selected actions
   *
   * @returns {Promise<AxiosResponse<T>>}
   */
  static getTagsForActions(actionIds = []) {
    // Using post due to the potential amount of actionIds
    return api.post(
      '/actions/client/tags',
      { actionIds },
    );
  }

  /**
   * Tag one or more actions with tags
   * @param clientActionIds
   * @param tags
   * @returns {JQuery.jqXHR | JQueryXHR | AxiosPromise<any>}
   */
  static tag(clientActionIds, tags) {
    let params = {
      clientActionIds,
      tags,
    };
    let endpoint = 'tags/bulk';

    if (!Array.isArray(clientActionIds)) {
      endpoint = `${clientActionIds}/tags`;
      params = {
        clientActionId: clientActionIds,
        tags,
      };
    }

    return api.post(`/actions/client/${endpoint}`, params);
  }

  /**
   *
   * @param clientActionId
   * @returns {*}
   */
  static getTeamManagers(clientActionId) {
    return api.get(`/actions/client/${clientActionId}/managers`);
  }

  /**
   * Responds to a query
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static respondToQuery({ clientActionId, note }) {
    return api.post(`/actions/client/${clientActionId}/status/query/respond`, { clientActionId, note });
  }

  /**
   * Rejects an action
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static submitQuery({
    clientActionId, query, teamId, userId = null,
  }) {
    return api.post(`/actions/client/${clientActionId}/status/query`, {
      clientActionId, query, userId, teamId,
    });
  }

  /**
   * Escalates  a query
   *
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static escalateQuery({
    clientActionId, query, teamId, userId,
  }) {
    return api.post(`/actions/client/${clientActionId}/status/query/escalate`, {
      clientActionId, query, teamId, userId,
    });
  }

  /**
   * Rejects an action
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static respondToNoAccess({ clientActionId, note }) {
    return api.post(`/actions/client/${clientActionId}/status/no-access/respond`, { clientActionId, note });
  }

  /**
   * Rejects an action
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static noAccess({ clientActionId, note }) {
    return api.post(`/actions/client/${clientActionId}/status/no-access`, { clientActionId, note });
  }

  /**
   * Rejects an action
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static reject({ clientActionId, note }) {
    return api.post(`/actions/client/${clientActionId}/reject`, { clientActionId, note });
  }


  /**
   * Adds a note against an action
   * @param clientActionId
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static addNote({ clientActionId, note }) {
    return api.post(`/actions/client/${clientActionId}/note`, { note });
  }

  /**
   * Adds a note against multiple actions
   *
   * @param clientActionIds
   * @param note
   * @returns { AxiosPromise<any>}
   */
  static bulkAddNote(clientActionIds, note) {
    return api.post('/actions/client/note', { clientActionIds, note });
  }

  static bulkUpdateWorkOrders(type, params) {
    return api.post(`/actions/${type}/work-orders/bulk`, params);
  }

  /**
   * Approves an action
   *
   * @param clientActionId
   * @param note
   * @param files
   * @returns { AxiosPromise<any>}
   */
  static approve({ clientActionId, note = null, files = null }) {
    const params = {
      clientActionId,
    };

    if (note) {
      params.note = note;
    }

    if (files) {
      params.files = files;
    }

    return api.post(`/actions/client/${clientActionId}/approve`, params);
  }

  /**
   * Bulk approve multiple actions
   * @param clientActionIds
   * @param note
   * @param files
   * @returns { AxiosPromise<any>}
   */
  static bulkApprove({ clientActionIds, note = null, files = null }) {
    return api.post('/actions/client/approve/bulk', { clientActionIds, note, files });
  }

  /**
   * Partially complete an action
   *
   * @param clientActionId
   * @returns { AxiosPromise<any>}
   */
  static partiallyComplete(clientActionId, note) {
    return api.post(`/actions/client/${clientActionId}/partially-complete`, { note });
  }

  /**
   * Downloads PDF of actions
   * @param clientActionIds
   * @param reviewer
   * @returns { AxiosPromise<any>}
   */
  static downloadPdf({ clientActionIds, reviewer }) {
    return api.post('/actions/client/download', {
      clientActionIds, reviewer,
    }, {
      responseType: 'arraybuffer',
    }).then((response) => {
      const filename = `report_${dayjs().format('YYYY-MM-DD').toString()}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
    });
  }

  /**
   * Retrieve photos relating to the action (recommendation)
   * @param clientActionId
   * @returns {*}
   */
  static getPhotos({ clientActionId }) {
    return api.get(`/actions/client/${clientActionId}/photos`);
  }

  /**
   * Retrieve photos relating to the action (recommendation)
   * @param clientActionId
   * @returns {*}
   */
  static getNotes({ clientActionId }) {
    return api.get(`/v1/actions/client/${clientActionId}/notes`);
  }

  /**
   * Download all actions
   * @param complianceTypeId
   * @returns {*}
   */
  static downloadAll(complianceTypeId) {
    return api.get(`/actions/client/download?complianceTypeId=${complianceTypeId}`);
  }
}

export default ClientAction;
